import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    fetchTasks, createTask, toggleTask, updateTask, deleteTask,
    fetchHabits, createHabit, toggleHabit, updateHabit, deleteHabit, coverHabit,
    fetchReminders, createReminder, toggleReminder, updateReminder, deleteReminder,
    fetchListItems, createListItem, toggleListItem, updateListItem, deleteListItem,
    fetchSettings, fetchStats,
} from "@/lib/api";
import { undoableDelete } from "@/lib/undoDelete";
import { celebrateWin } from "@/lib/wins";
import { maybeAskNotificationPermission } from "@/lib/notifications";

// useItems owns all of today's collections (tasks/habits/reminders/list items)
// + the settings + stats snapshot. It exposes CRUD handlers wired to the
// existing optimistic-update / undo-delete patterns and emits the `onCelebrate`
// callback when an item completion warrants a Mochi reaction.
//
//   const items = useItems({ today, onCelebrate, onWin });
//
// `onCelebrate(event, ctx)` is called for completion of a task/habit so the
// host (App.js) can drive the avatar speech bubble.
// `onWin(winKey)` is optional — invoked when the backend toggle returns a
// win key (e.g. first_task, three_tasks). The hook also calls celebrateWin
// for the toast, so onWin is purely for any *additional* host reaction.
export function useItems({ today, onCelebrate }) {
    const [settings, setSettingsState] = useState(null);
    const [stats, setStats] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [habits, setHabits] = useState([]);
    const [reminders, setReminders] = useState([]);
    const [listItems, setListItems] = useState([]);

    const loadAll = useCallback(async () => {
        const [s, st, t, h, r, l] = await Promise.all([
            fetchSettings(),
            fetchStats(),
            fetchTasks(today),
            fetchHabits(),
            fetchReminders(today),
            fetchListItems(),
        ]);
        setSettingsState(s);
        setStats(st);
        setTasks(t);
        setHabits(h);
        setReminders(r);
        setListItems(l);
        return { settings: s, stats: st };
    }, [today]);

    const refreshStats = useCallback(async () => {
        setStats(await fetchStats());
    }, []);

    // Pull fresh settings — used after any completion since the backend awards
    // rice balls on each item completion / pomodoro.
    const refreshSettings = useCallback(async () => {
        setSettingsState(await fetchSettings());
    }, []);

    // -- task handlers ------------------------------------------------------
    const handleToggleTask = useCallback(async (t) => {
        const res = await toggleTask(t.id, !t.completed);
        const next = res?.task || res;
        setTasks((prev) => prev.map((x) => (x.id === t.id ? next : x)));
        await refreshStats();
        if (next.completed) {
            refreshSettings();  // jar got +1
            onCelebrate?.("task_completed", { task_title: next.title });
            if (res?.win) celebrateWin(res.win);
        }
    }, [onCelebrate, refreshStats, refreshSettings]);

    const handleDeleteTask = useCallback((t) => {
        undoableDelete({
            label: "Task",
            item: t,
            apiDelete: deleteTask,
            onLocalRemove: () => setTasks((prev) => prev.filter((x) => x.id !== t.id)),
            onLocalRestore: () => setTasks((prev) => (prev.some((x) => x.id === t.id) ? prev : [...prev, t])),
            onCommit: refreshStats,
        });
        refreshStats();
    }, [refreshStats]);

    // -- habit handlers -----------------------------------------------------
    const handleToggleHabit = useCallback(async (h) => {
        const res = await toggleHabit(h.id);
        const next = res?.habit || res;
        setHabits((prev) => prev.map((x) => (x.id === h.id ? next : x)));
        await refreshStats();
        if (next.completed_dates.includes(today)) {
            refreshSettings();  // jar got +1
            onCelebrate?.("habit_completed", { task_title: next.title });
            if (res?.win) celebrateWin(res.win);
        }
    }, [onCelebrate, refreshStats, refreshSettings, today]);

    const handleDeleteHabit = useCallback((h) => {
        undoableDelete({
            label: "Habit",
            item: h,
            apiDelete: deleteHabit,
            onLocalRemove: () => setHabits((prev) => prev.filter((x) => x.id !== h.id)),
            onLocalRestore: () => setHabits((prev) => (prev.some((x) => x.id === h.id) ? prev : [...prev, h])),
            onCommit: refreshStats,
        });
        refreshStats();
    }, [refreshStats]);

    // Cover-pass: Mochi uses her weekly pass on this habit. Returns
    // { habit, settings, win:"cover" } from the backend, which lets the
    // host trigger the wink animation + accept-toast.
    const handleCoverHabit = useCallback(async (h) => {
        const res = await coverHabit(h.id);
        const next = res?.habit;
        if (next) {
            setHabits((prev) => prev.map((x) => (x.id === h.id ? next : x)));
        }
        if (res?.settings) {
            setSettingsState(res.settings);
        }
        await refreshStats();
        return res;
    }, [refreshStats]);

    // -- reminder handlers --------------------------------------------------
    const handleToggleReminder = useCallback(async (r) => {
        const next = await toggleReminder(r.id, !r.completed);
        setReminders((prev) => prev.map((x) => (x.id === r.id ? next : x)));
        refreshStats();
        if (next.completed && !r.completed) refreshSettings();
    }, [refreshStats, refreshSettings]);

    // Inline edit (currently used to update reminder time from the home list)
    const handleEditReminder = useCallback(async (r, patch) => {
        try {
            const next = await updateReminder(r.id, patch);
            setReminders((prev) => prev.map((x) => (x.id === r.id ? next : x)));
            if (patch.time) toast(`⏰ ${next.title} moved to ${next.time}`, { duration: 3000 });
            else if (patch.title) toast(`✏️ renamed → ${next.title}`, { duration: 2500 });
        } catch (e) {
            toast("couldn't update reminder", { duration: 3000 });
        }
    }, []);

    // Inline edit for a habit (rename or schedule_days).
    const handleEditHabit = useCallback(async (h, patch) => {
        try {
            const next = await updateHabit(h.id, patch);
            setHabits((prev) => prev.map((x) => (x.id === h.id ? next : x)));
            if (patch.title) {
                toast(`✏️ renamed → ${next.title}`, { duration: 2500 });
            } else if (patch.schedule_days) {
                const labels = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
                const summary = next.schedule_days?.length === 7
                    ? "every day"
                    : next.schedule_days.map((d) => labels[d]).join(" ");
                toast(`📅 ${next.title}: ${summary}`, { duration: 2500 });
            }
        } catch {
            toast("couldn't update habit", { duration: 3000 });
        }
    }, []);

    // Inline edit for a task (title or priority).
    const handleEditTask = useCallback(async (t, patch) => {
        try {
            const res = await updateTask(t.id, patch);
            const next = res?.task || res;
            setTasks((prev) => prev.map((x) => (x.id === t.id ? next : x)));
            if (patch.title) toast(`✏️ renamed → ${next.title}`, { duration: 2500 });
            else if (patch.priority) toast(`✏️ ${next.title}: ${next.priority}`, { duration: 2500 });
        } catch {
            toast("couldn't update task", { duration: 3000 });
        }
    }, []);

    // Inline edit for a list item (title or quantity).
    const handleEditList = useCallback(async (i, patch) => {
        try {
            const next = await updateListItem(i.id, patch);
            setListItems((prev) => prev.map((x) => (x.id === i.id ? next : x)));
            if (patch.title) toast(`✏️ renamed → ${next.title}`, { duration: 2500 });
            else if (patch.quantity != null) toast(`✏️ ${next.title}: ${next.quantity || "—"}`, { duration: 2500 });
        } catch {
            toast("couldn't update item", { duration: 3000 });
        }
    }, []);

    const handleDeleteReminder = useCallback((r) => {
        undoableDelete({
            label: "Reminder",
            item: r,
            apiDelete: deleteReminder,
            onLocalRemove: () => setReminders((prev) => prev.filter((x) => x.id !== r.id)),
            onLocalRestore: () => setReminders((prev) => (prev.some((x) => x.id === r.id) ? prev : [...prev, r])),
            onCommit: refreshStats,
        });
        refreshStats();
    }, [refreshStats]);

    // -- list-item handlers -------------------------------------------------
    const handleToggleList = useCallback(async (i) => {
        const next = await toggleListItem(i.id, !i.completed);
        setListItems((prev) => prev.map((x) => (x.id === i.id ? next : x)));
        if (next.completed && !i.completed) refreshSettings();
    }, [refreshSettings]);

    const handleDeleteList = useCallback((i) => {
        undoableDelete({
            label: "Item",
            item: i,
            apiDelete: deleteListItem,
            onLocalRemove: () => setListItems((prev) => prev.filter((x) => x.id !== i.id)),
            onLocalRestore: () => setListItems((prev) => (prev.some((x) => x.id === i.id) ? prev : [...prev, i])),
        });
    }, []);

    // -- quick-add (shared add-from-sheet handler) --------------------------
    const handleQuickSubmit = useCallback(async ({ type, data }) => {
        try {
            if (type === "task") {
                const t = await createTask(data);
                // Recurring tasks whose first occurrence is in the future
                // are intentionally hidden from today's list. Only append
                // to local state if the task IS visible today — otherwise
                // it would briefly flash in the list before `loadAll`
                // filtered it out.
                const recType = t.recurrence_type || "none";
                const nxt = t.next_occurrence;
                if (recType === "none" || !nxt || nxt <= today) {
                    setTasks((p) => [...p, t]);
                }
            } else if (type === "habit") {
                const h = await createHabit(data);
                setHabits((p) => [...p, h]);
            } else if (type === "reminder") {
                const r = await createReminder(data);
                setReminders((p) => [...p, r]);
                const perm = await maybeAskNotificationPermission();
                if (perm === "denied") {
                    toast("Browser notifications are blocked — you'll still get in-app pings.", { icon: "🔕" });
                }
            } else if (type === "list") {
                const i = await createListItem(data);
                setListItems((p) => [...p, i]);
            }
            await refreshStats();
            toast.success("Added ✨");
        } catch {
            toast.error("Couldn't add that");
        }
    }, [refreshStats, today]);

    // Habits scheduled for today (Mon=0 ... Sun=6 in backend)
    const todaysHabits = useMemo(() => {
        const wd = new Date().getDay(); // 0=Sun
        const tIdx = (wd + 6) % 7;       // → Mon=0
        return habits.filter((h) =>
            (h.schedule_days || []).includes(tIdx)
            && (!h.starts_on || h.starts_on <= today)
        );
    }, [habits, today]);

    return {
        settings, setSettings: setSettingsState,
        stats, setStats,
        tasks, habits, reminders, listItems,
        setReminders,
        todaysHabits,
        loadAll,
        refreshStats,
        handleToggleTask, handleDeleteTask, handleEditTask,
        handleToggleHabit, handleDeleteHabit, handleEditHabit, handleCoverHabit,
        handleToggleReminder, handleDeleteReminder, handleEditReminder,
        handleToggleList, handleDeleteList, handleEditList,
        handleQuickSubmit,
    };
}
